import { SfLog } from '@app/adapters/logger'
import { reidentifyAppcuesUser } from './reidentify-appcues-user'

const STATUS_PAGE_FETCH_TIMEOUT_MS = 5000

interface StatusPageResponse {
  status?: {
    indicator?: 'none' | 'minor' | 'major' | 'critical'
  }
}

// Sets the Appcues user trait `statusPageIncidentActive` based on the Silverfin
// status page. The pNPS Appcues flow uses this trait as an exclusion criterion
// so surveys are suppressed while an incident is active.
//
// On failure we do NOT re-identify with `false`: a prior load during the same
// incident may have set the trait to `true`, and writing `false` would
// overwrite it.
export async function identifyStatusPageIncident(): Promise<void> {
  const statusUrl = window.AppcuesSettings?.statusPageUrl
  if (!statusUrl) {
    return
  }

  const timeoutController = new AbortController()
  const timeoutId = window.setTimeout(
    () => timeoutController.abort(),
    STATUS_PAGE_FETCH_TIMEOUT_MS
  )
  try {
    const response = await fetch(statusUrl, {
      signal: timeoutController.signal,
    })
    if (!response.ok) {
      SfLog.warn('Status page status.json returned a non-2xx response', {
        extra: { status: response.status },
      })
      return
    }

    const body = (await response.json()) as StatusPageResponse
    const indicator = body?.status?.indicator
    if (!indicator) {
      SfLog.warn('Status page response missing status.indicator', {
        extra: { body },
      })
      return
    }

    reidentifyAppcuesUser(
      { statusPageIncidentActive: indicator !== 'none' },
      'Failed to re-identify status page incident state'
    )
  } catch (error) {
    // `extra` nesting is required for the logger's network-error filter to drop
    // the "Failed to fetch" TypeErrors when statuspage.io is blocked.
    SfLog.warn('Failed to identify status page incident state', {
      extra: { error },
    })
  } finally {
    window.clearTimeout(timeoutId)
  }
}
